import _ from "lodash";
import moment from "moment";

import buildFetchDuck from "../../vendor/signal-utils/build-fetch-duck";
import chainReducers from "../../vendor/signal-utils/chain-reducers";
import apiUrl from "../../api-url";

const STORE_MOUNT_POINT = "search";
const duck = buildFetchDuck("search");

// query parameter builders
const getBasicQueryString = (queryParameter, filterValue) => {
  return `&${queryParameter}=${encodeURIComponent(filterValue)}`;
};

const getDateRangeQueryString = (queryParameter, filterValue) => {
  // Add on up to two query parameters -- the start and end dates in filterValue
  let url = "";

  if (filterValue.from != null) {
    url += `&${queryParameter}_start=${moment(filterValue.from).unix()}`;
  }
  if (filterValue.to != null) {
    url += `&${queryParameter}_end=${moment(filterValue.to).unix()}`;
  }

  return url;
};

const getLocationQueryString = (queryParameter, filterValue, state) => {
  // Map locations in the search string to their IDs using state
  const locations = state.locations.data;
  const matchingLocations = getMatchingLocationIDs(locations, filterValue);
  const matchingLocationsStr = matchingLocations.join();

  return `&${queryParameter}=${encodeURIComponent(matchingLocationsStr)}`;
};

const getEverythingQueryString = (queryParameter, filterValue, state) => {
  // Map locations in the search string to their IDs using state
  const locations = state.locations.data;
  const matchingLocations = getMatchingLocationIDs(locations, filterValue);

  // Append location ids to initial search value
  const queryStr =
    matchingLocations.length > 0 && filterValue !== matchingLocations[0]
      ? `${filterValue},${matchingLocations.join()}`
      : filterValue;

  return `&${queryParameter}=${encodeURIComponent(queryStr)}`;
};

// Helper for query builders
const getMatchingLocationIDs = (locations, searchValue) => {
  // If the search value is a number, the search is for a location ID directly
  if (_.isNumber(searchValue)) {
    return [searchValue];
  }

  const matchingLocations = locations.filter(l => {
    return (
      (l.name && l.name.toLowerCase().includes(searchValue.toLowerCase())) ||
      (l.city && l.city.toLowerCase().includes(searchValue.toLowerCase())) ||
      (l.state && l.state.toLowerCase().includes(searchValue.toLowerCase())) ||
      (l.code && l.code.toLowerCase().includes(searchValue.toLowerCase()))
    );
  });

  return matchingLocations.map(l => l.id);
};

// Search and Filter Categories
const SEARCH_CATEGORIES = {
  everything: {
    label: "Search Everything",
    placeholder: "Search Location, Shipment ID, Part #, or VIN #",
    queryBuilder: (val, state) =>
      getEverythingQueryString("search_everything_values", val, state)
  },
  location: {
    label: "Location",
    placeholder: "Search Location",
    queryBuilder: (val, state) =>
      getLocationQueryString("shipper_location_ids", val, state)
  },
  shipment_id: {
    label: "Shipment ID",
    placeholder: "Search Shipment ID",
    queryBuilder: val => getBasicQueryString("creator_shipment_id", val)
  },
  part_number: {
    label: "Part #",
    placeholder: "Search Part #",
    queryBuilder: val => getBasicQueryString("part_values", val)
  },
  vin: {
    label: "VIN",
    placeholder: "Search VIN #",
    queryBuilder: val => getBasicQueryString("vin_values", val)
  }
};

const FILTER_CATEGORIES = {
  origin: {
    label: "Origin",
    queryBuilder: val => getBasicQueryString("origin_id", val)
  },
  destination: {
    label: "Destination",
    queryBuilder: val => getBasicQueryString("destination_id", val)
  },
  carrier: {
    label: "Carrier",
    queryBuilder: val => getBasicQueryString("carrier", val)
  },
  pickup: {
    label: "Pickup Date",
    queryBuilder: val => getDateRangeQueryString("pickup", val)
  },
  delivery: {
    label: "Delivery Date",
    queryBuilder: val => getDateRangeQueryString("delivery", val)
  },
  mode: {
    label: "Mode",
    queryBuilder: val => getBasicQueryString("shipment_mode", val)
  },
  location_type: {
    label: "Location Type",
    queryBuilder: val => getBasicQueryString("location_type", val)
  },
  status: {
    label: "Status",
    queryBuilder: val => getBasicQueryString("current_status_id", val)
  },
  active_exception: {
    label: "Active Exception",
    queryBuilder: val => getBasicQueryString("exception_id", val)
  }
};

// URLS
const SHIPMENT_SEARCH_URL = apiUrl("/shipping-ng/shipments");

// actions
const SET_SEARCH_OBJ = "search/SET_SEARCH_OBJ";
const SET_SEARCH_CATEGORY = "search/SET_SEARCH_CATEGORY";
const SET_SEARCH_FILTER = "search/SET_SEARCH_FILTER";
const CLEAR_SEARCH_FILTER = "search/CLEAR_SEARCH_FILTER";
const CLEAR_SEARCH_FILTERS = "search/CLEAR_SEARCH_FILTERS";
const RESET_SEARCH = "search/RESET_SEARCH";

const SET_PAGINATION = "search/SET_PAGINATION";
const SET_SORT = "search/SET_SORT";

// fetchers and action creators
const searchShipments = (
  searchObj,
  searchCategory,
  searchFilters,
  resetPagination = true
) => {
  return (dispatch, getState) => {
    // Reset pagination to the first page, unless this search is triggered by a pagination change
    if (resetPagination) {
      dispatch({ type: SET_PAGINATION, page: 0, pageSize: 20 });
    }

    const state = getState();
    const page = getPage(state);
    const pageSize = getPageSize(state);
    const sortColumn = getSortColumn(state);
    const reverseSort = getReverseSort(state);

    let url =
      SHIPMENT_SEARCH_URL + `?page_number=${page}&page_size=${pageSize}`;

    if (sortColumn != null) {
      url += `&sort_column=${sortColumn}&reverse_sort=${reverseSort ? 1 : 0}`;
    }

    if (searchObj.value) {
      let category = SEARCH_CATEGORIES[searchCategory];
      url += category.queryBuilder(searchObj.value, getState());
    }

    Object.keys(searchFilters).forEach(f => {
      const filterValue = searchFilters[f];
      const filterCategory = FILTER_CATEGORIES[f];

      if (filterValue != null) {
        url += filterCategory.queryBuilder(filterValue);
      }
    });

    console.log(url);
    dispatch(duck.fetch(url));
  };
};

const setPagination = (page, pageSize) => {
  return (dispatch, getState) => {
    const state = getState();
    const searchObj = getSearchObj(state);
    const searchCategory = getSearchCategory(state);
    const searchFilters = getSearchFilters(state);

    dispatch({ type: SET_PAGINATION, page, pageSize });
    dispatch(searchShipments(searchObj, searchCategory, searchFilters, false));
  };
};

const setSort = (sortColumn, reverse = false) => {
  return (dispatch, getState) => {
    const state = getState();
    const searchObj = getSearchObj(state);
    const searchCategory = getSearchCategory(state);
    const searchFilters = getSearchFilters(state);

    dispatch({ type: SET_SORT, sortColumn, reverse });
    dispatch(searchShipments(searchObj, searchCategory, searchFilters, false));
  };
};

const setSearch = (label, value) => {
  return dispatch =>
    dispatch({ type: SET_SEARCH_OBJ, searchObj: { label, value } });
};

const setSearchCategory = category => {
  return dispatch =>
    dispatch({ type: SET_SEARCH_CATEGORY, searchCategory: category });
};

const setSearchFilter = (key, value) => {
  return dispatch => dispatch({ type: SET_SEARCH_FILTER, key, value });
};

const clearSearchFilter = key => {
  return dispatch => dispatch({ type: CLEAR_SEARCH_FILTER, key });
};

const clearSearchFilters = () => {
  return dispatch => dispatch({ type: CLEAR_SEARCH_FILTERS });
};

const resetSearch = () => {
  return dispatch => dispatch({ type: RESET_SEARCH });
};

// selectors
const getShipments = state => state.search.data.data || [];
const getSearchObj = state => state.search.searchObj;
const getSearchCategory = state => state.search.searchCategory;
const getSearchFilters = state => state.search.searchFilters;
const getIsLoading = state => state.search.isLoading || false;
const getPage = state => state.search.page;
const getPageSize = state => state.search.pageSize;
const getSortColumn = state => state.search.sortColumn;
const getReverseSort = state => state.search.reverseSort;
const getTotalPages = state =>
  state.search.data.meta ? state.search.data.meta["total-pages"] : 0;
const getTotalShipments = state => state.search.data.recordsFiltered || 0;

// initial state
const initialState = {
  ...duck.initialState,
  searchObj: { label: "", value: "" },
  searchCategory: "everything",
  searchFilters: {},
  page: 0,
  pageSize: 20,
  sortColumn: null,
  reverseSort: false
};

// reducer
const SearchReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SET_SEARCH_OBJ:
      return { ...state, searchObj: action.searchObj };
    case SET_SEARCH_CATEGORY:
      return { ...state, searchCategory: action.searchCategory };
    case SET_SEARCH_FILTER:
      return {
        ...state,
        searchFilters: { ...state.searchFilters, [action.key]: action.value }
      };
    case CLEAR_SEARCH_FILTER:
      return {
        ...state,
        searchFilters: _.omit(state.searchFilters, action.key)
      };
    case CLEAR_SEARCH_FILTERS:
      return { ...state, searchFilters: {} };
    case RESET_SEARCH:
      return {
        ...state,
        searchObj: initialState.searchObj,
        searchCategory: initialState.searchCategory,
        searchFilters: initialState.searchFilters
      };
    case SET_PAGINATION:
      return { ...state, page: action.page, pageSize: action.pageSize };
    case SET_SORT:
      return {
        ...state,
        sortColumn: action.sortColumn,
        reverseSort: action.reverse
      };
    default:
      return state;
  }
};

// interface
export default {
  mountPoint: STORE_MOUNT_POINT,
  actionTypes: {},
  actionCreators: {
    searchShipments,
    setSearch,
    setSearchCategory,
    setSearchFilter,
    clearSearchFilter,
    clearSearchFilters,
    resetSearch,
    setPagination,
    setSort
  },
  selectors: {
    getShipments,
    getSearchObj,
    getSearchCategory,
    getSearchFilters,
    getIsLoading,
    getPage,
    getPageSize,
    getTotalPages,
    getTotalShipments
  },
  reducer: chainReducers([SearchReducer, duck.reducer]),
  SEARCH_CATEGORIES,
  FILTER_CATEGORIES
};



// WEBPACK FOOTER //
// ./src/modules/shipment-search/ShipmentSearchState.js